import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './MainLayout.css';
import theme from '../../config/theme';
import '../../config/pastelTheme.css';

const MainLayout = ({ children }) => {
  const [userData, setUserData] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Carregar dados do usuário
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setUserData(user);
    setIsAdmin(user.email === 'admin@example.com');
    
    // Verificar preferência de tema
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark') {
      setDarkMode(true);
      document.body.classList.add('theme-dark');
    }
  }, []);

  // Alternar entre tema claro e escuro
  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    if (!darkMode) {
      document.body.classList.add('theme-dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.body.classList.remove('theme-dark');
      localStorage.setItem('theme', 'light');
    }
  };

  // Alternar a barra lateral
  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
    // Adicionar ou remover classe no body para ajustar o layout
    if (!sidebarCollapsed) {
      document.body.classList.add('sidebar-collapsed');
    } else {
      document.body.classList.remove('sidebar-collapsed');
    }
  };

  // Verificar se o link está ativo
  const isActive = (path) => {
    return location.pathname === path;
  };

  // Manipular o clique no dropdown
  const handleDropdownToggle = (e) => {
    e.preventDefault();
    const dropdownMenu = e.currentTarget.nextElementSibling;
    dropdownMenu.classList.toggle('show');
  };

  // Função para fazer logout
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  return (
    <div className={`page ${darkMode ? 'theme-dark' : ''}`} style={{ backgroundColor: theme.colors.background }}>
      <div className="page-wrapper">
        {/* Barra lateral - aplicando cores pastel */}
        <aside className={`navbar navbar-vertical navbar-expand-lg navbar-dark ${sidebarCollapsed ? 'navbar-collapsed' : ''}`} style={{ backgroundColor: theme.colors.dark }}>
          <div className="container-fluid">
            <button className="navbar-toggler" type="button" onClick={toggleSidebar}>
              <span className="navbar-toggler-icon"></span>
            </button>
            <h1 className="navbar-brand navbar-brand-autodark">
              <Link to="/">
                <span className="navbar-brand-image" style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#fff' }}>Simullact.io</span>
              </Link>
            </h1>
            <div className="collapse navbar-collapse" id="sidebar-menu">
              <ul className="navbar-nav pt-lg-3" style={{ fontFamily: theme.typography.fontFamily }}>
                <li className={`nav-item ${isActive('/') ? 'active' : ''}`}>
                  <Link to="/" className="nav-link">
                    <span className="nav-link-icon d-md-none d-lg-inline-block">
                      <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <polyline points="5 12 3 12 12 3 21 12 19 12" />
                        <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-7" />
                        <path d="M9 21v-6a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v6" />
                      </svg>
                    </span>
                    <span className="nav-link-title">Dashboard</span>
                  </Link>
                </li>
                
                <li className={`nav-item ${isActive('/setup') ? 'active' : ''}`}>
                  <Link to="/setup" className="nav-link">
                    <span className="nav-link-icon d-md-none d-lg-inline-block">
                      <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M10.325 4.317c.426 -1.756 2.924 -1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543 -.94 3.31 .826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756 .426 1.756 2.924 0 3.35a1.724 1.724 0 0 0 -1.066 2.573c.94 1.543 -.826 3.31 -2.37 2.37a1.724 1.724 0 0 0 -2.572 1.065c-.426 1.756 -2.924 1.756 -3.35 0a1.724 1.724 0 0 0 -2.573 -1.066c-1.543 .94 -3.31 -.826 -2.37 -2.37a1.724 1.724 0 0 0 -1.065 -2.572c-1.756 -.426 -1.756 -2.924 0 -3.35a1.724 1.724 0 0 0 1.066 -2.573c-.94 -1.543 .826 -3.31 2.37 -2.37c1 .608 2.296 .07 2.572 -1.065z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    </span>
                    <span className="nav-link-title">Cenários</span>
                  </Link>
                </li>
                
                <li className={`nav-item ${isActive('/reports') ? 'active' : ''}`}>
                  <Link to="/reports" className="nav-link">
                    <span className="nav-link-icon d-md-none d-lg-inline-block">
                      <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <polyline points="9 11 12 14 20 6" />
                        <path d="M20 12v6a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2h9" />
                      </svg>
                    </span>
                    <span className="nav-link-title">Relatórios</span>
                  </Link>
                </li>
                
                <li className={`nav-item ${isActive('/withdrawals') ? 'active' : ''}`}>
                  <Link to="/withdrawals" className="nav-link">
                    <span className="nav-link-icon d-md-none d-lg-inline-block">
                      <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M17 8v-3a1 1 0 0 0 -1 -1h-10a2 2 0 0 0 0 4h12a1 1 0 0 1 1 1v3m0 4v3a1 1 0 0 1 -1 1h-12a2 2 0 0 1 -2 -2v-12" />
                        <path d="M20 12v4h-4a2 2 0 0 1 0 -4h4" />
                      </svg>
                    </span>
                    <span className="nav-link-title">Extrato de Retiradas</span>
                  </Link>
                </li>
                
                <li className={`nav-item ${isActive('/calendar') ? 'active' : ''}`}>
                  <Link to="/calendar" className="nav-link">
                    <span className="nav-link-icon d-md-none d-lg-inline-block">
                      <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <rect x="4" y="5" width="16" height="16" rx="2" />
                        <line x1="16" y1="3" x2="16" y2="7" />
                        <line x1="8" y1="3" x2="8" y2="7" />
                        <line x1="4" y1="11" x2="20" y2="11" />
                        <line x1="11" y1="15" x2="12" y2="15" />
                        <line x1="12" y1="15" x2="12" y2="18" />
                      </svg>
                    </span>
                    <span className="nav-link-title">Calendário</span>
                  </Link>
                </li>

                <li className={`nav-item ${isActive('/agents') ? 'active' : ''}`}>
                  <Link to="/agents" className="nav-link">
                    <span className="nav-link-icon d-md-none d-lg-inline-block">
                      <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M3 12h4l3 8l4 -16l3 8h4" />
                      </svg>
                    </span>
                    <span className="nav-link-title">Agents / Specs</span>
                  </Link>
                </li>

                {/* Menu dropdown de Configurações do Sistema */}
                <li className={`nav-item dropdown ${
                  isActive('/system-settings') || 
                  isActive('/admin/email-config') || 
                  isActive('/admin/sequelize') || 
                  isActive('/admin/error-logs') ? 'active' : ''
                }`}>
                  <a 
                    href="#" 
                    className="nav-link dropdown-toggle" 
                    onClick={handleDropdownToggle}
                    aria-expanded="false"
                  >
                    <span className="nav-link-icon d-md-none d-lg-inline-block">
                      <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M10.325 4.317c.426 -1.756 2.924 -1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543 -.94 3.31 .826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756 .426 1.756 2.924 0 3.35a1.724 1.724 0 0 0 -1.066 2.573c.94 1.543 -.826 3.31 -2.37 2.37a1.724 1.724 0 0 0 -2.572 1.065c-.426 1.756 -2.924 1.756 -3.35 0a1.724 1.724 0 0 0 -2.573 -1.066c-1.543 .94 -3.31 -.826 -2.37 -2.37a1.724 1.724 0 0 0 -1.065 -2.572c-1.756 -.426 -1.756 -2.924 0 -3.35a1.724 1.724 0 0 0 1.066 -2.573c-.94 -1.543 .826 -3.31 2.37 -2.37c1 .608 2.296 .07 2.572 -1.065z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    </span>
                    <span className="nav-link-title">Configurações do Sistema</span>
                  </a>
                  <div className="dropdown-menu">
                    <Link to="/system-settings" className="dropdown-item">
                      Configurações Gerais
                    </Link>
                    <Link to="/admin/email-config" className="dropdown-item">
                      Configuração de Email
                    </Link>
                    <Link to="/admin/sequelize" className="dropdown-item">
                      Dashboard do Sequelize
                    </Link>
                    <Link to="/admin/error-logs" className="dropdown-item">
                      Logs de Erros
                    </Link>
                    </div>
                </li>

                {/* Link para Base de Conhecimento */}
                <li className={`nav-item ${isActive('/admin/knowledge-base') ? 'active' : ''}`}>
                  <Link to="/admin/knowledge-base" className="nav-link">
                    <span className="nav-link-icon d-md-none d-lg-inline-block">
                      <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M3 19a9 9 0 0 1 9 0a9 9 0 0 1 9 0" />
                        <path d="M3 6a9 9 0 0 1 9 0a9 9 0 0 1 9 0" />
                        <path d="M3 6l0 13" />
                        <path d="M12 6l0 13" />
                        <path d="M21 6l0 13" />
                      </svg>
                    </span>
                    <span className="nav-link-title">Base de Conhecimento</span>
                  </Link>
                </li>

                {/* Link para Dashboard de Conhecimento */}
                <li className={`nav-item ${isActive('/admin/knowledge-dashboard') ? 'active' : ''}`}>
                  <Link to="/admin/knowledge-dashboard" className="nav-link">
                    <span className="nav-link-icon d-md-none d-lg-inline-block">
                      <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <rect x="3" y="7" width="18" height="13" rx="2" />
                        <path d="M8 7v-2a2 2 0 0 1 2 -2h4a2 2 0 0 1 2 2v2" />
                        <path d="M12 12l0 .01" />
                        <path d="M3 13a20 20 0 0 0 18 0" />
                      </svg>
                    </span>
                    <span className="nav-link-title">Dashboard de Conhecimento</span>
                  </Link>
                </li>

                {/* Link para Logs de Erros de API */}
                <li className={`nav-item ${isActive('/admin/api-error-logs') ? 'active' : ''}`}>
                  <Link to="/admin/api-error-logs" className="nav-link">
                    <span className="nav-link-icon d-md-none d-lg-inline-block">
                      <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M9 9v-1a3 3 0 0 1 6 0v1" />
                        <path d="M8 9h8a6 6 0 0 1 1 3v3a5 5 0 0 1 -10 0v-3a6 6 0 0 1 1 -3" />
                        <path d="M3 13l2 -2" />
                        <path d="M3 13l2 2" />
                        <path d="M21 13l-2 -2" />
                        <path d="M21 13l-2 2" />
                      </svg>
                    </span>
                    <span className="nav-link-title">Logs de Erros de API</span>
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </aside>
        
        {/* Conteúdo principal */}
        <div className="page-main">
          {/* Header */}
          <header className="navbar navbar-expand-md navbar-light d-print-none" style={{ 
            backgroundColor: 'white', 
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            fontFamily: theme.typography.fontFamily 
          }}>
            <div className="container-xl">
              <button className="navbar-toggler" type="button" onClick={toggleSidebar}>
                <span className="navbar-toggler-icon"></span>
              </button>
              <h1 className="navbar-brand navbar-brand-autodark d-none-navbar-horizontal pe-0 pe-md-3 d-md-none">
                <Link to="/">
                  <span className="navbar-brand-image" style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#000' }}>Simullact.io</span>
                </Link>
              </h1>
              
              <div className="navbar-nav flex-row order-md-last ms-auto">
                <div className="nav-item me-3">
                  <button className="nav-link px-0" onClick={toggleDarkMode}>
                    {darkMode ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <circle cx="12" cy="12" r="4" />
                        <path d="M3 12h1m8 -9v1m8 8h1m-9 8v1m-6.4 -15.4l.7 .7m12.1 -.7l-.7 .7m0 11.4l.7 .7m-12.1 -.7l-.7 .7" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M12 3c.132 0 .263 0 .393 0a7.5 7.5 0 0 0 7.92 12.446a9 9 0 1 1 -8.313 -12.454z" />
                      </svg>
                    )}
                  </button>
                </div>
                
                {/* Botão de Logout - Visível em todos os tamanhos de tela */}
                <div className="nav-item me-3">
                  <button className="pastel-btn pastel-btn-secondary" onClick={handleLogout}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-logout" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                      <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                      <path d="M14 8v-2a2 2 0 0 0 -2 -2h-7a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h7a2 2 0 0 0 2 -2v-2"></path>
                      <path d="M7 12h14l-3 -3m0 6l3 -3"></path>
                    </svg>
                    <span className="d-none d-md-inline ms-1">Sair</span>
                  </button>
                </div>
                
                <div className="nav-item dropdown">
                  <a href="#" className="nav-link d-flex lh-1 text-reset p-0" onClick={handleDropdownToggle}>
                    <span className="avatar avatar-sm" style={{ backgroundColor: theme.colors.primary }}>
                      {userData.name ? userData.name.charAt(0).toUpperCase() : 'U'}
                    </span>
                    <div className="d-none d-xl-block ps-2">
                      <div style={{ fontFamily: theme.typography.fontFamily }}>{userData.name || 'Usuário'}</div>
                      <div className="mt-1 small text-muted">{userData.email || 'usuario@exemplo.com'}</div>
                    </div>
                  </a>
                  <div className="dropdown-menu dropdown-menu-end">
                    <a href="#" className="dropdown-item">Perfil</a>
                    <div className="dropdown-divider"></div>
                    <button className="dropdown-item" onClick={handleLogout}>
                      <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-logout me-2" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                        <path d="M14 8v-2a2 2 0 0 0 -2 -2h-7a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h7a2 2 0 0 0 2 -2v-2"></path>
                        <path d="M7 12h14l-3 -3m0 6l3 -3"></path>
                      </svg>
                      Sair
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </header>
          
          {/* Conteúdo da página */}
          <div className="page-content">
            {children}
          </div>
          
          {/* Footer */}
          <footer className="footer footer-transparent d-print-none" style={{ fontFamily: theme.typography.fontFamily }}>
            <div className="container-xl">
              <div className="row text-center align-items-center flex-row-reverse">
                <div className="col-12 col-lg-auto mt-3 mt-lg-0">
                  <ul className="list-inline list-inline-dots mb-0">
                    <li className="list-inline-item">
                      Copyright © {new Date().getFullYear()} Simullact.io - Plataforma de Investimentos
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default MainLayout; 